/**
 * Folder access — three entries, one output (DI-2-1 §2.2): a list of image
 * files, each with the path relative to what the pilot chose, plus a COUNT of
 * what was left out ("2 non-image files skipped" — counted, never silent).
 *
 *   1. drop           — `DataTransferItem.webkitGetAsEntry()`, walked recursively
 *                       (an SD card root has `DCIM/…` below it)
 *   2. <input webkitdirectory multiple> — the fallback without the FSA picker
 *                       (Firefox / Safari read-only arm, `04` §9.9)
 *   3. FSA picker     — a `FileSystemDirectoryHandle`, iterated recursively
 *
 * Only `.jpg` / `.jpeg` (any case) are kept. Output is sorted by relative path
 * so the same card gives the same order in every browser.
 *
 * DI-3-S75 §3.1 — besides the images, a gimbal's `*_pos.txt` (any case,
 * ≤ `POS_FILE_MAX_BYTES`, the first by name of each folder level) is kept in
 * `aux`: it is no image (never parsed, grouped, counted in a set) and no
 * longer counted as skipped; it rides the set's upload session to the DA's
 * `aux/` area. A second one in the same folder, or one over the cap, is
 * skipped and counted like any other non-image. Without one, the result is
 * exactly what it was before (no `aux` key).
 */

export interface CollectedFile {
  file: File;
  relativePath: string;
}

export interface CollectResult {
  files: CollectedFile[];
  /**
   * DI-3-S75 — the folder's `*_pos.txt` files (at most one per folder level,
   * sorted by relative path); absent when there is none.
   */
  aux?: CollectedFile[];
  /** Non-image entries left out. */
  skipped: number;
  /** The top-level folder name, when there was exactly one. */
  folderName: string | null;
}

export function isImageName(name: string): boolean {
  return /\.jpe?g$/i.test(name);
}

/** DI-3-S75 — the size cap of a kept `*_pos.txt` (1 MB). */
export const POS_FILE_MAX_BYTES = 1024 * 1024;

/** DI-3-S75 — a gimbal position file's name (`DJI_…_pos.txt`, any case). */
export function isPosFileName(name: string): boolean {
  return /_pos\.txt$/i.test(name);
}

/** The folder part of a relative path ("" for a file at the top level). */
export function folderOf(relativePath: string): string {
  const i = relativePath.lastIndexOf("/");
  return i < 0 ? "" : relativePath.slice(0, i);
}

function byPath(a: CollectedFile, b: CollectedFile): number {
  return a.relativePath < b.relativePath
    ? -1
    : a.relativePath > b.relativePath
      ? 1
      : 0;
}

function finish(
  files: CollectedFile[],
  skipped: number,
  folderName: string | null,
  posCandidates: CollectedFile[] = [],
): CollectResult {
  files.sort(byPath);
  // DI-3-S75 — at most one `*_pos.txt` per folder level (the first by name);
  // the others, and any over the cap, are skipped like any non-image
  const aux: CollectedFile[] = [];
  const seen = new Set<string>();
  for (const c of posCandidates.slice().sort(byPath)) {
    const folder = folderOf(c.relativePath);
    if (c.file.size > POS_FILE_MAX_BYTES || seen.has(folder)) {
      skipped++;
      continue;
    }
    seen.add(folder);
    aux.push(c);
  }
  return aux.length > 0
    ? { files, aux, skipped, folderName }
    : { files, skipped, folderName };
}

function rootOf(paths: readonly string[]): string | null {
  const roots = new Set(
    paths.map((p) => (p.includes("/") ? p.split("/")[0] : "")),
  );
  if (roots.size !== 1) return null;
  const [only] = [...roots];
  // "" (a file at the top level) is no folder
  return only === undefined || only === "" ? null : only;
}

// --- 1. drop ----------------------------------------------------------------

/** The subset of the File and Directory Entries API the walk needs. */
export interface EntryLike {
  isFile: boolean;
  isDirectory: boolean;
  name: string;
  fullPath: string;
  file?: (ok: (f: File) => void, err?: (e: unknown) => void) => void;
  createReader?: () => {
    readEntries: (
      ok: (entries: EntryLike[]) => void,
      err?: (e: unknown) => void,
    ) => void;
  };
}

function fileOf(entry: EntryLike): Promise<File> {
  return new Promise((resolve, reject) => {
    if (!entry.file) {
      reject(new Error("not a file entry"));
      return;
    }
    entry.file(resolve, reject);
  });
}

async function readAll(entry: EntryLike): Promise<EntryLike[]> {
  const reader = entry.createReader?.();
  if (!reader) return [];
  const out: EntryLike[] = [];
  // Chrome returns at most 100 entries per call: read until an empty batch
  for (;;) {
    const batch = await new Promise<EntryLike[]>((resolve, reject) => {
      reader.readEntries(resolve, reject);
    });
    if (batch.length === 0) return out;
    out.push(...batch);
  }
}

export async function filesFromEntries(
  entries: readonly EntryLike[],
): Promise<CollectResult> {
  const files: CollectedFile[] = [];
  const pos: CollectedFile[] = [];
  let skipped = 0;
  const walk = async (entry: EntryLike): Promise<void> => {
    if (entry.isDirectory) {
      for (const child of await readAll(entry)) await walk(child);
      return;
    }
    if (!entry.isFile) return;
    const image = isImageName(entry.name);
    if (!image && !isPosFileName(entry.name)) {
      skipped++;
      return;
    }
    const file = await fileOf(entry);
    (image ? files : pos).push({
      file,
      relativePath: entry.fullPath.replace(/^\/+/, "") || entry.name,
    });
  };
  for (const e of entries) await walk(e);
  const folder =
    entries.length === 1 && entries[0]?.isDirectory ? entries[0].name : null;
  return finish(files, skipped, folder, pos);
}

/**
 * DI-3-S18-FE — Chromium's `DataTransferItem.getAsFileSystemHandle()`: a
 * dropped folder as a real `FileSystemDirectoryHandle` (remembered like a
 * picked one). Not in every TS DOM lib, so it is typed here, like
 * `RootDirLike.resolve` (DI-3-S16-FE); its absence is guarded.
 */
type DropItem = DataTransferItem & {
  webkitGetAsEntry?: () => EntryLike | null;
  getAsFileSystemHandle?: () => Promise<unknown>;
};

/** What a drop read: the files, and — a single dropped folder — its handle. */
export interface DropResult extends CollectResult {
  /**
   * DI-3-S18-FE — the dropped folder's directory handle, where the browser
   * has `getAsFileSystemHandle` and exactly ONE directory was dropped;
   * absent for loose files, several folders, or another browser.
   */
  handle?: DirectoryHandleLike;
}

function isDirectoryHandle(h: unknown, name: string): h is DirectoryHandleLike {
  const d = h as Partial<DirectoryHandleLike> | null;
  return (
    d !== null &&
    typeof d === "object" &&
    d.kind === "directory" &&
    d.name === name &&
    typeof d.values === "function"
  );
}

/**
 * The drop's files (the entries API, walked recursively — unchanged) and,
 * DI-3-S18-FE, the handle of a single dropped folder. Everything the drop's
 * items are asked for is asked SYNCHRONOUSLY, before the first `await`: the
 * browser empties `DataTransfer.items` once the drop event has returned, so
 * the caller must call this from inside the drop handler (`DropStep.onDrop`
 * does). The handle's promise is awaited after the walk.
 */
export async function filesFromDataTransfer(
  dt: Pick<DataTransfer, "items" | "files">,
): Promise<DropResult> {
  const entries: EntryLike[] = [];
  const owners: DropItem[] = [];
  const items = Array.from(dt.items) as DropItem[];
  for (const item of items) {
    if (item.kind !== "file") continue;
    const getEntry = item.webkitGetAsEntry;
    const entry = typeof getEntry === "function" ? getEntry.call(item) : null;
    if (entry) {
      entries.push(entry);
      owners.push(item);
    }
  }
  // DI-3-S18-FE — one dropped folder: its handle, asked in the same tick
  const only = entries.length === 1 ? entries[0] : undefined;
  const owner = owners[0];
  const getHandle = owner?.getAsFileSystemHandle;
  const pending: Promise<unknown> | null =
    only?.isDirectory && owner && typeof getHandle === "function"
      ? (() => {
          try {
            return Promise.resolve(getHandle.call(owner)).catch(() => null);
          } catch {
            return null;
          }
        })()
      : null;
  if (entries.length === 0) return filesFromList(Array.from(dt.files));
  const result = await filesFromEntries(entries);
  const handle = pending ? await pending : null;
  return only && isDirectoryHandle(handle, only.name)
    ? { ...result, handle }
    : result;
}

// --- 2. <input webkitdirectory> / plain multiple ----------------------------

export function filesFromList(list: readonly File[]): CollectResult {
  const files: CollectedFile[] = [];
  const pos: CollectedFile[] = [];
  let skipped = 0;
  for (const file of list) {
    const image = isImageName(file.name);
    if (!image && !isPosFileName(file.name)) {
      skipped++;
      continue;
    }
    const rel = (file as File & { webkitRelativePath?: string })
      .webkitRelativePath;
    (image ? files : pos).push({
      file,
      relativePath: rel && rel.length > 0 ? rel : file.name,
    });
  }
  return finish(files, skipped, rootOf(files.map((f) => f.relativePath)), pos);
}

// --- 3. FSA directory handle ----------------------------------------------

/** The subset of `FileSystemDirectoryHandle` the walk needs (a test double satisfies it). */
export interface DirectoryHandleLike {
  kind: "directory";
  name: string;
  values: () => AsyncIterable<DirectoryHandleLike | FileHandleLike>;
}
export interface FileHandleLike {
  kind: "file";
  name: string;
  getFile: () => Promise<File>;
}

export async function filesFromDirectoryHandle(
  root: DirectoryHandleLike,
): Promise<CollectResult> {
  const files: CollectedFile[] = [];
  const pos: CollectedFile[] = [];
  let skipped = 0;
  const walk = async (
    dir: DirectoryHandleLike,
    prefix: string,
  ): Promise<void> => {
    for await (const h of dir.values()) {
      const path = `${prefix}/${h.name}`;
      if (h.kind === "directory") {
        await walk(h, path);
      } else if (isImageName(h.name)) {
        files.push({ file: await h.getFile(), relativePath: path });
      } else if (isPosFileName(h.name)) {
        pos.push({ file: await h.getFile(), relativePath: path });
      } else {
        skipped++;
      }
    }
  };
  await walk(root, root.name);
  return finish(files, skipped, root.name, pos);
}
